(() => {
  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const clampIndex = (index, total) => {
    const size = Math.max(1, total);
    return (index + size) % size;
  };

  const getSectionRoots = () => Array.from(document.querySelectorAll('[data-pdp-545]'));

  const updateUrlParam = (key, value) => {
    if (!window.history?.replaceState) return;
    const url = new URL(window.location.href);

    if (value) {
      url.searchParams.set(key, value);
    } else {
      url.searchParams.delete(key);
    }

    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  };

  const PRIVATE_SALE_ACCESS_STORAGE_KEY = 'fivefourfive_private_sale_access';

  const hasStoredPrivateSaleAccess = (sectionEl) => {
    const accessHash = sectionEl?.dataset?.privateSaleAccessHash || '';
    if (!accessHash) return false;

    try {
      return !!(window.localStorage && window.localStorage.getItem(PRIVATE_SALE_ACCESS_STORAGE_KEY) === accessHash);
    } catch (error) {
      return false;
    }
  };

  const hasStorefrontPasswordAccess = () => {
    try {
      return /(?:^|;\s*)storefront_digest=/.test(document.cookie || '');
    } catch (error) {
      return false;
    }
  };

  const unlockPrivateSaleSoftAccess = (sectionEl) => {
    if (!sectionEl || sectionEl.dataset.privateSaleSoftAccess !== 'true') return;
    if (
      sectionEl.dataset.privateSaleAuthorized !== 'true' &&
      !hasStoredPrivateSaleAccess(sectionEl) &&
      !hasStorefrontPasswordAccess()
    ) {
      return;
    }

    sectionEl.dataset.privateSaleSoftUnlocked = 'true';

    sectionEl.querySelectorAll('[data-private-sale-soft-target]').forEach((target) => {
      target.hidden = false;
      target.classList.remove('product-preorder--private-sale-soft-locked');
    });

    sectionEl.querySelectorAll('[data-private-sale-soft-fallback]').forEach((fallback) => {
      fallback.hidden = true;
    });
  };

  const getSizeFieldset = (sectionEl) => {
    const fieldsets = Array.from(sectionEl.querySelectorAll('variant-radios fieldset'));
    return fieldsets.find((fieldset) => {
      const input = fieldset.querySelector('input[type="radio"]');
      const inputName = input?.name?.toLowerCase() || '';
      return inputName.includes('size') || inputName.includes('taglia');
    }) || null;
  };

  const hasPendingSizeSelection = (sectionEl) => {
    if (hasStaticVariantPicker(sectionEl)) return false;
    const sizeFieldset = getSizeFieldset(sectionEl);
    if (!sizeFieldset) return false;
    return !sizeFieldset.querySelector('input[type="radio"]:checked');
  };

  const hasStaticVariantPicker = (sectionEl) =>
    !!sectionEl?.querySelector?.('variant-radios[data-disable-variant-interaction="true"], variant-selects[data-disable-variant-interaction="true"]');

  const shouldHideRestockNotify = (sectionEl) => sectionEl?.dataset.hideRestockNotify === 'true';

  const getInitialVariant = (sectionEl) => {
    const id =
      sectionEl.dataset.initialVariantId ||
      sectionEl.querySelector('form[id^="product-form-"]:not([id^="kowi-sticky-product-form-"]) input[name="id"]')?.value ||
      sectionEl.querySelector('.kowi-sticky-atc input[name="id"]')?.value;

    if (!id) return null;

    const inventoryQuantity = parseInt(sectionEl.dataset.initialVariantInventoryQuantity || '0', 10);

    return {
      id,
      available: sectionEl.dataset.initialVariantAvailable === 'true',
      inventory_management: sectionEl.dataset.initialVariantInventoryManagement || null,
      inventory_quantity: Number.isNaN(inventoryQuantity) ? 0 : inventoryQuantity
    };
  };

  const scrollToSizePicker = (sectionEl) => {
    const sizeFieldset = getSizeFieldset(sectionEl);
    if (!sizeFieldset) return;

    sizeFieldset.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };

  const resetInitialSizeSelection = (sectionEl) => {
    if (!sectionEl || sectionEl.dataset.sizeSelectionReset === 'true') return;
    if (hasStaticVariantPicker(sectionEl)) return;

    const sizeFieldset = getSizeFieldset(sectionEl);
    if (!sizeFieldset) return;

    sizeFieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = false;
      input.removeAttribute('checked');
    });

    sizeFieldset.querySelectorAll('label.pill-variant.is-selected').forEach((label) => {
      label.classList.remove('is-selected');
    });

    sectionEl.querySelectorAll('form[id^="product-form-"] input[name="id"]').forEach((input) => {
      input.value = '';
    });

    const stickyInput = sectionEl.querySelector('.kowi-sticky-atc input[name="id"]');
    if (stickyInput) stickyInput.value = '';

    sectionEl.dataset.sizeSelectionReset = 'true';
  };

  const getSelectedVariantFromJSON = (sectionEl) => {
    const jsonEl = sectionEl.querySelector('variant-radios script[type="application/json"], variant-selects script[type="application/json"]');
    if (!jsonEl) return getInitialVariant(sectionEl);

    let variants;
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (error) {
      return null;
    }
    if (!Array.isArray(variants)) return null;

    let selectedOptions = [];
    const radios = sectionEl.querySelector('variant-radios');
    const selects = sectionEl.querySelector('variant-selects');

    if (radios) {
      selectedOptions = Array.from(radios.querySelectorAll('fieldset')).map((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });
    } else if (selects) {
      selectedOptions = Array.from(selects.querySelectorAll('select')).map((select) => select.value);
    }

    if (!selectedOptions.length) return getInitialVariant(sectionEl);
    if (selectedOptions.some((value) => value === null)) return null;

    return variants.find((variant) => selectedOptions.every((option, index) => variant.options && variant.options[index] == option)) || null;
  };

  const findRealNotifyTrigger = (sectionEl) => {
    const mainForm =
      sectionEl.querySelector('form[id^="product-form-"]:not([id^="kowi-sticky-product-form-"])') ||
      sectionEl.querySelector('.product__info-container form[id^="product-form-"]');

    if (!mainForm) return null;

    return (
      mainForm.querySelector('#BIS_trigger') ||
      mainForm.querySelector('.BIS_trigger') ||
      mainForm.querySelector('[data-bis-trigger]')
    );
  };

  const openNotifyModal = (sectionEl) => {
    const realTrigger = findRealNotifyTrigger(sectionEl);
    if (realTrigger) {
      realTrigger.click();
      return true;
    }
    return false;
  };

  const updateStickyFromVariant = (sectionEl, variant) => {
    const sticky = sectionEl.querySelector('.kowi-sticky-atc');
    if (!sticky) return;

    const stickyForm = sticky.querySelector('form');
    const stickyInput = stickyForm?.querySelector('input[name="id"]');
    const stickyBtn = stickyForm?.querySelector('[data-kowi-sticky-btn]') || stickyForm?.querySelector('#kowi-sticky-atc-btn');
    const stickySpan = stickyBtn?.querySelector('span');

    if (!stickyInput || !stickyBtn) return;

    stickyBtn.onclick = null;
    stickyBtn.disabled = false;
    stickyBtn.hidden = false;

    if (hasPendingSizeSelection(sectionEl)) {
      sticky.classList.remove('is-hidden');
      stickyInput.value = '';
      stickyInput.disabled = true;
      stickyBtn.type = 'button';
      stickyBtn.removeAttribute('name');
      stickyBtn.dataset.mode = 'select-size';
      if (stickySpan) stickySpan.textContent = 'Select size';
      stickyBtn.onclick = (event) => {
        event.preventDefault();
        scrollToSizePicker(sectionEl);
      };
      return;
    }

    if (variant?.id) stickyInput.value = variant.id;
    stickyInput.disabled = false;
    stickyBtn.setAttribute('name', 'add');
    stickyBtn.dataset.mode = variant?.available ? 'add-to-cart' : 'notify';

    if (variant?.available) {
      sticky.classList.remove('is-hidden');
      stickyBtn.type = 'submit';
      if (stickySpan) stickySpan.textContent = window.variantStrings?.addToCart || 'Add to cart';
      return;
    }

    if (shouldHideRestockNotify(sectionEl)) {
      sticky.classList.add('is-hidden');
      document.body.classList.remove('kowi-has-sticky-atc');
      stickyBtn.hidden = true;
      stickyBtn.type = 'button';
      stickyBtn.removeAttribute('name');
      stickyBtn.dataset.mode = 'notify';
      return;
    }

    sticky.classList.add('is-hidden');
    document.body.classList.remove('kowi-has-sticky-atc');
    stickyBtn.type = 'button';
    stickyBtn.removeAttribute('name');
    if (stickySpan) stickySpan.textContent = 'Notify me when restocked';
    stickyBtn.onclick = (event) => {
      event.preventDefault();
      openNotifyModal(sectionEl);
    };
  };

  const updateMainButtonFromVariant = (sectionEl, variant) => {
    const mainForm =
      sectionEl.querySelector('form[id^="product-form-"]:not([id^="kowi-sticky-product-form-"])') ||
      sectionEl.querySelector('.product__info-container form[id^="product-form-"]');

    const mainBtn = mainForm?.querySelector('.product-form__submit');
    const mainSpan = mainBtn?.querySelector('span');
    const variantInput = mainForm?.querySelector('input[name="id"]');
    if (!mainBtn || !mainSpan) return;

    mainBtn.onclick = null;
    mainBtn.hidden = false;

    if (hasPendingSizeSelection(sectionEl)) {
      if (variantInput) {
        variantInput.value = '';
        variantInput.disabled = true;
      }
      mainBtn.type = 'button';
      mainBtn.removeAttribute('name');
      mainBtn.disabled = false;
      mainBtn.removeAttribute('disabled');
      mainBtn.dataset.mode = 'select-size';
      mainSpan.textContent = 'Select size';
      mainBtn.onclick = (event) => {
        event.preventDefault();
        scrollToSizePicker(sectionEl);
      };
      return;
    }

    if (variantInput) variantInput.disabled = false;

    if (variant?.available) {
      mainBtn.type = 'submit';
      mainBtn.setAttribute('name', 'add');
      mainBtn.disabled = false;
      mainBtn.removeAttribute('disabled');
      mainBtn.dataset.mode = 'add-to-cart';
      mainSpan.textContent = window.variantStrings?.addToCart || 'Add To Cart';
      return;
    }

    if (shouldHideRestockNotify(sectionEl)) {
      if (variantInput) variantInput.disabled = true;
      mainBtn.hidden = true;
      mainBtn.type = 'button';
      mainBtn.removeAttribute('name');
      mainBtn.disabled = true;
      mainBtn.setAttribute('disabled', 'disabled');
      mainBtn.dataset.mode = 'notify';
      return;
    }

    mainBtn.type = 'button';
    mainBtn.removeAttribute('name');
    mainBtn.disabled = false;
    mainBtn.removeAttribute('disabled');
    mainBtn.dataset.mode = 'notify';
    mainSpan.textContent = 'Notify me when restocked';
    mainBtn.onclick = (event) => {
      event.preventDefault();
      openNotifyModal(sectionEl);
    };
  };

  const computeStockState = (variant, lowThreshold) => {
    const available = !!(variant && variant.available);
    if (!available) return 'out';

    const tracked = !!(variant && variant.inventory_management);
    if (!tracked) return 'instock';

    const qty = typeof variant.inventory_quantity === 'number'
      ? variant.inventory_quantity
      : parseInt(variant.inventory_quantity || '0', 10);

    if (!Number.isNaN(qty) && qty > 0 && qty <= lowThreshold) return 'low';
    return 'instock';
  };

  const updateStockIndicator = (sectionEl, variant) => {
    const indicator = sectionEl.querySelector('[data-stock-indicator]');
    if (!indicator) return;

    const lowThreshold = parseInt(indicator.dataset.lowThreshold || '5', 10);
    const state = computeStockState(variant, lowThreshold);
    indicator.classList.remove('is-instock', 'is-low', 'is-out');

    const textEl = indicator.querySelector('.kowi-stock__text');
    if (!textEl) return;

    if (state === 'out') {
      indicator.classList.add('is-out');
      textEl.textContent = indicator.dataset.outOfStockText || '';
      return;
    }

    if (state === 'low') {
      indicator.classList.add('is-low');
      textEl.textContent = indicator.dataset.lowStockText || '';
      return;
    }

    indicator.classList.add('is-instock');
    textEl.textContent = indicator.dataset.inStockText || '';
  };

  const syncVariantDrivenUi = (sectionEl) => {
    const variant = getSelectedVariantFromJSON(sectionEl);
    updateMainButtonFromVariant(sectionEl, variant);
    updateStickyFromVariant(sectionEl, variant);
    if (!variant) return;
    updateStockIndicator(sectionEl, variant);
  };

  const initKowiGallery = (root) => {
    if (!root || root.dataset.kowiGalleryBound === 'true') return;
    root.dataset.kowiGalleryBound = 'true';

    const rail = root.querySelector('.kowi-gallery__rail');
    const slides = Array.from(root.querySelectorAll('.kowi-gallery__slide'));
    const btnPrev = root.querySelector('.kowi-gallery__arrow--prev');
    const btnNext = root.querySelector('.kowi-gallery__arrow--next');
    const current = root.querySelector('.kowi-gallery__current');
    const progress = root.querySelector('.kowi-gallery__progress span');
    const total = slides.length;
    if (!rail || total === 0) return;

    let index = 0;
    rail.classList.add('is-initial');

    const render = () => {
      rail.style.transform = `translateX(${-index * 100}%)`;
      if (current) current.textContent = String(index + 1);
      if (progress) {
        progress.style.transform = `scaleX(${(index + 1) / total})`;
      }
    };

    const show = (nextIndex) => {
      index = clampIndex(nextIndex, total);
      render();
    };

    btnPrev?.addEventListener('click', () => show(index - 1));
    btnNext?.addEventListener('click', () => show(index + 1));

    let startX = null;
    rail.addEventListener('touchstart', (event) => {
      if (!event.touches?.[0]) return;
      startX = event.touches[0].clientX;
    }, { passive: true });

    rail.addEventListener('touchend', (event) => {
      if (startX === null) return;
      const endX = event.changedTouches?.[0] ? event.changedTouches[0].clientX : startX;
      const dx = endX - startX;
      startX = null;
      if (Math.abs(dx) > 40) show(dx > 0 ? index - 1 : index + 1);
    }, { passive: true });

    root.setAttribute('tabindex', '0');
    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') show(index - 1);
      if (event.key === 'ArrowRight') show(index + 1);
    });

    render();
    requestAnimationFrame(() => rail.classList.remove('is-initial'));
  };

  const initPdpFullscreenGallery = (sectionEl) => {
    if (!sectionEl || sectionEl.dataset.pdpLightboxBound === 'true') return;
    sectionEl.dataset.pdpLightboxBound = 'true';

    const gallery = sectionEl.querySelector('.kowi-gallery');
    if (!gallery) return;

    const sourceSlides = Array.from(gallery.querySelectorAll('.kowi-gallery__slide'));
    const sourceImages = sourceSlides
      .map((slide) => slide.querySelector('img'))
      .filter(Boolean);

    if (!sourceImages.length) return;

    const getLargeSrc = (image) => {
      const src = image.currentSrc || image.src || '';
      return src.replace(/([?&]width=)\d+/, (_match, prefix) => `${prefix}2400`);
    };

    const lightbox = document.createElement('div');
    lightbox.className = 'kowi-pdp-lightbox';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-label', 'Product images');
    lightbox.hidden = true;

    lightbox.innerHTML = `
      <button class="kowi-pdp-lightbox__close" type="button" aria-label="Close fullscreen images">
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M6 6L18 18M18 6L6 18" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="square"></path>
        </svg>
      </button>
      <button class="kowi-pdp-lightbox__arrow kowi-pdp-lightbox__arrow--prev" type="button" aria-label="Previous image">
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M10.5 3.5L6 8l4.5 4.5" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="square"></path>
        </svg>
      </button>
      <div class="kowi-pdp-lightbox__viewport">
        <div class="kowi-pdp-lightbox__rail"></div>
      </div>
      <button class="kowi-pdp-lightbox__arrow kowi-pdp-lightbox__arrow--next" type="button" aria-label="Next image">
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M5.5 3.5L10 8l-4.5 4.5" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="square"></path>
        </svg>
      </button>
      <div class="kowi-pdp-lightbox__counter" aria-live="polite">
        <span class="kowi-pdp-lightbox__current">1</span>
        <span>/</span>
        <span class="kowi-pdp-lightbox__total">${sourceImages.length}</span>
      </div>
    `;

    const rail = lightbox.querySelector('.kowi-pdp-lightbox__rail');
    const closeButton = lightbox.querySelector('.kowi-pdp-lightbox__close');
    const prevButton = lightbox.querySelector('.kowi-pdp-lightbox__arrow--prev');
    const nextButton = lightbox.querySelector('.kowi-pdp-lightbox__arrow--next');
    const currentCounter = lightbox.querySelector('.kowi-pdp-lightbox__current');
    const total = sourceImages.length;
    let index = 0;
    let scale = 1;
    let panX = 0;
    let panY = 0;
    let sourcePointerStartX = 0;
    let sourcePointerStartY = 0;
    let sourcePointerMoved = false;
    let activePointers = new Map();
    let pointerStartX = 0;
    let pointerStartY = 0;
    let startPanX = 0;
    let startPanY = 0;
    let pinchStartDistance = 0;
    let pinchStartScale = 1;
    let pinchStartPanX = 0;
    let pinchStartPanY = 0;
    let pinchStartCenterX = 0;
    let pinchStartCenterY = 0;
    let moved = false;
    let isPointerDown = false;
    let isPinching = false;
    let didPinch = false;

    const slides = sourceImages.map((image, imageIndex) => {
      const slide = document.createElement('div');
      const wrap = document.createElement('div');
      const lightboxImage = document.createElement('img');

      slide.className = 'kowi-pdp-lightbox__slide';
      wrap.className = 'kowi-pdp-lightbox__image-wrap';
      lightboxImage.src = getLargeSrc(image);
      lightboxImage.alt = image.alt || '';
      lightboxImage.loading = imageIndex === 0 ? 'eager' : 'lazy';
      lightboxImage.draggable = false;
      wrap.appendChild(lightboxImage);
      slide.appendChild(wrap);
      rail.appendChild(slide);
      return slide;
    });

    const getActiveSlide = () => slides[index] || null;
    const getActiveWrap = () => getActiveSlide()?.querySelector('.kowi-pdp-lightbox__image-wrap') || null;
    const getActivePointerPair = () => Array.from(activePointers.values()).slice(0, 2);

    const getPointerDistance = () => {
      const [first, second] = getActivePointerPair();
      if (!first || !second) return 0;
      return Math.hypot(second.x - first.x, second.y - first.y);
    };

    const getPointerCenter = () => {
      const [first, second] = getActivePointerPair();
      if (!first || !second) return { x: 0, y: 0 };
      return {
        x: (first.x + second.x) / 2,
        y: (first.y + second.y) / 2
      };
    };

    const applyZoom = () => {
      const wrap = getActiveWrap();
      if (!wrap) return;
      wrap.style.transform = `translate3d(${panX}px, ${panY}px, 0) scale(${scale})`;
      wrap.classList.toggle('is-zoomed', scale > 1);
    };

    const resetZoom = () => {
      scale = 1;
      panX = 0;
      panY = 0;
      slides.forEach((slide) => {
        const wrap = slide.querySelector('.kowi-pdp-lightbox__image-wrap');
        if (!wrap) return;
        wrap.style.transform = 'translate3d(0px, 0px, 0) scale(1)';
        wrap.classList.remove('is-zoomed');
      });
    };

    const show = (nextIndex) => {
      index = clampIndex(nextIndex, total);
      resetZoom();
      rail.style.transform = `translateX(${-index * 100}%)`;
      if (currentCounter) currentCounter.textContent = String(index + 1);
    };

    const close = () => {
      lightbox.hidden = true;
      lightbox.classList.remove('is-open');
      document.documentElement.classList.remove('kowi-pdp-lightbox-open');
      document.body.classList.remove('kowi-pdp-lightbox-open');
      activePointers.clear();
      isPointerDown = false;
      isPinching = false;
      didPinch = false;
      resetZoom();
    };

    const open = (startIndex) => {
      lightbox.hidden = false;
      requestAnimationFrame(() => lightbox.classList.add('is-open'));
      document.documentElement.classList.add('kowi-pdp-lightbox-open');
      document.body.classList.add('kowi-pdp-lightbox-open');
      show(startIndex);
      closeButton?.focus({ preventScroll: true });
    };

    const toggleZoom = () => {
      if (scale > 1) {
        resetZoom();
        return;
      }
      scale = 2.25;
      panX = 0;
      panY = 0;
      applyZoom();
    };

    gallery.addEventListener('pointerdown', (event) => {
      sourcePointerMoved = false;
      sourcePointerStartX = event.clientX;
      sourcePointerStartY = event.clientY;
    });

    gallery.addEventListener('pointermove', (event) => {
      const dx = event.clientX - sourcePointerStartX;
      const dy = event.clientY - sourcePointerStartY;
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) sourcePointerMoved = true;
    });

    sourceSlides.forEach((slide, slideIndex) => {
      slide.addEventListener('click', (event) => {
        if (event.target.closest('.kowi-gallery__arrow')) return;
        if (!event.target.closest('img')) return;
        if (sourcePointerMoved) {
          sourcePointerMoved = false;
          return;
        }
        open(slideIndex);
      });
    });

    closeButton?.addEventListener('click', close);
    prevButton?.addEventListener('click', () => show(index - 1));
    nextButton?.addEventListener('click', () => show(index + 1));

    lightbox.addEventListener('click', (event) => {
      if (event.target === lightbox) close();
    });

    rail.addEventListener('pointerdown', (event) => {
      const wrap = event.target.closest('.kowi-pdp-lightbox__image-wrap');
      if (!wrap) return;

      activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      wrap.setPointerCapture?.(event.pointerId);

      if (activePointers.size >= 2) {
        const center = getPointerCenter();
        isPinching = true;
        didPinch = true;
        moved = true;
        pinchStartDistance = getPointerDistance();
        pinchStartScale = scale;
        pinchStartPanX = panX;
        pinchStartPanY = panY;
        pinchStartCenterX = center.x;
        pinchStartCenterY = center.y;
        return;
      }

      didPinch = false;
      isPointerDown = true;
      moved = false;
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
      startPanX = panX;
      startPanY = panY;
    });

    rail.addEventListener('pointermove', (event) => {
      if (activePointers.has(event.pointerId)) {
        activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      }

      if (isPinching && activePointers.size >= 2) {
        event.preventDefault();
        const center = getPointerCenter();
        const nextDistance = getPointerDistance();
        const nextScale = pinchStartDistance > 0 ? pinchStartScale * (nextDistance / pinchStartDistance) : scale;
        scale = Math.min(3.5, Math.max(1, nextScale));
        panX = scale > 1 ? pinchStartPanX + center.x - pinchStartCenterX : 0;
        panY = scale > 1 ? pinchStartPanY + center.y - pinchStartCenterY : 0;
        applyZoom();
        return;
      }

      if (!isPointerDown) return;
      const dx = event.clientX - pointerStartX;
      const dy = event.clientY - pointerStartY;
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) moved = true;

      if (scale > 1) {
        event.preventDefault();
        panX = startPanX + dx;
        panY = startPanY + dy;
        applyZoom();
      }
    });

    rail.addEventListener('pointerup', (event) => {
      const wasPinching = isPinching || didPinch;
      const wasPointerDown = isPointerDown;
      const dx = event.clientX - pointerStartX;
      event.target.closest('.kowi-pdp-lightbox__image-wrap')?.releasePointerCapture?.(event.pointerId);
      activePointers.delete(event.pointerId);

      if (wasPinching) {
        if (activePointers.size < 2) isPinching = false;
        if (activePointers.size === 0) didPinch = false;
        if (scale <= 1.03) resetZoom();
        isPointerDown = false;
        return;
      }

      if (!wasPointerDown) return;
      isPointerDown = false;

      if (scale <= 1 && Math.abs(dx) > 50) {
        show(dx > 0 ? index - 1 : index + 1);
        return;
      }

      if (!moved && event.target.closest('.kowi-pdp-lightbox__image-wrap')) {
        toggleZoom();
      }
    });

    rail.addEventListener('pointercancel', (event) => {
      event.target.closest('.kowi-pdp-lightbox__image-wrap')?.releasePointerCapture?.(event.pointerId);
      activePointers.delete(event.pointerId);
      isPointerDown = false;
      isPinching = false;
    });

    rail.addEventListener('wheel', (event) => {
      if (!lightbox.classList.contains('is-open')) return;
      event.preventDefault();
      const nextScale = Math.min(3, Math.max(1, scale + (event.deltaY < 0 ? 0.2 : -0.2)));
      if (nextScale === 1) {
        resetZoom();
        return;
      }
      scale = nextScale;
      applyZoom();
    }, { passive: false });

    document.addEventListener('keydown', (event) => {
      if (!lightbox.classList.contains('is-open')) return;
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') show(index - 1);
      if (event.key === 'ArrowRight') show(index + 1);
    });

    document.body.appendChild(lightbox);
  };

  const initCompleteLook = (sectionEl) => {
    if (!sectionEl) return;

    sectionEl.querySelectorAll('[data-complete-look]').forEach((block) => {
      if (block.dataset.completeLookBound === 'true') return;
      block.dataset.completeLookBound = 'true';

      const hiddenInputs = Array.from(block.querySelectorAll('[data-complete-look-input]'));
      const sizeInputs = Array.from(block.querySelectorAll('[data-complete-look-option]'));
      if (!hiddenInputs.length) return;

      const syncHiddenInputs = (variantId) => {
        block.dataset.selectedVariantId = variantId || '';
        hiddenInputs.forEach((input) => {
          input.value = variantId || '';
        });
        updateUrlParam('complete_variant', variantId || '');
      };

      const selectSize = (input) => {
        if (!input || input.disabled) return;
        input.checked = true;
        sizeInputs.forEach((sizeInput) => {
          const label = block.querySelector(`label[for="${sizeInput.id}"]`);
          label?.classList.toggle('is-selected', sizeInput === input);
        });
        syncHiddenInputs(input.dataset.variantId || '');
      };

      sizeInputs.forEach((input) => {
        input.addEventListener('change', () => selectSize(input));
      });

      block.addEventListener('click', (event) => {
        const label = event.target.closest('.complete-look__size-option');
        if (!label || !block.contains(label)) return;

        const inputId = label.getAttribute('for');
        const input = inputId ? document.getElementById(inputId) : null;
        if (!input || input.disabled) return;

        event.preventDefault();
        selectSize(input);
      });

      const initialInput = sizeInputs.find((input) => input.checked && !input.disabled);
      if (initialInput) {
        selectSize(initialInput);
      } else if (sizeInputs.length) {
        sizeInputs.forEach((input) => {
          input.checked = false;
          const label = block.querySelector(`label[for="${input.id}"]`);
          label?.classList.remove('is-selected');
        });
        syncHiddenInputs('');
      }
    });
  };

  const getCartAddUrl = () => {
    const cartAddUrl = window.routes?.cart_add_url || '/cart/add';
    return cartAddUrl.endsWith('.js') ? cartAddUrl : `${cartAddUrl}.js`;
  };

  const getCompleteLookVariantId = (sectionEl) => {
    const urlVariant = new URLSearchParams(window.location.search).get('complete_variant');
    if (urlVariant) return Number(urlVariant);

    const selectedBlockVariantId = sectionEl
      ?.querySelector('[data-complete-look][data-selected-variant-id]:not([data-selected-variant-id=""])')
      ?.dataset.selectedVariantId;
    if (selectedBlockVariantId) return Number(selectedBlockVariantId);

    const selectedInputVariantId = sectionEl
      ?.querySelector('[data-complete-look-option]:checked:not(:disabled)')
      ?.dataset.variantId;
    if (selectedInputVariantId) return Number(selectedInputVariantId);

    const selectedLabelVariantId = sectionEl
      ?.querySelector('[data-complete-look-size-option].is-selected')
      ?.dataset.variantId;
    if (selectedLabelVariantId) return Number(selectedLabelVariantId);

    return 0;
  };

  const getMainFormVariantId = (form) => {
    const formVariant = form?.querySelector('input[name="id"]')?.value;
    return Number(formVariant) || Number(new URLSearchParams(window.location.search).get('variant')) || 0;
  };

  const openCartAfterCompleteLookAdd = async (response) => {
    document.dispatchEvent(new CustomEvent('fivefourfive:cart-added', {
      detail: response
    }));

    const cartDrawer = window.FIVEFOURFIVE_CART_DRAWER;
    if (cartDrawer && typeof cartDrawer.refresh === 'function') {
      await cartDrawer.refresh();
      cartDrawer.open?.();
      return;
    }

    const drawer = document.getElementById('CartDrawer545');
    if (drawer) {
      try {
        const res = await fetch('/?sections=cart-drawer-545', {
          credentials: 'same-origin',
          cache: 'no-store'
        });
        const data = await res.json();
        const html = data['cart-drawer-545'];

        if (html) {
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer545');
          if (fresh) drawer.innerHTML = fresh.innerHTML;
        }

        drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('cart-545--open');
        window.FIVEFOURFIVE_RECENTLY_VIEWED?.render(drawer);
        return;
      } catch (error) {
        // Fall through to the native cart link fallback.
      }
    }

    document.querySelector('a[href*="/cart"], button[aria-label*="cart" i]')?.click();
  };

  const initCompleteLookAddToCartGuard = () => {
    if (document.documentElement.dataset.completeLookAddGuardBound === 'true') return;
    document.documentElement.dataset.completeLookAddGuardBound = 'true';

    let addInFlight = false;

    const getEligibleBundle = (form) => {
      const sectionEl = form?.closest('[data-pdp-545]');
      if (!sectionEl) return null;

      const mainVariantId = getMainFormVariantId(form);
      const completeLookVariantId = getCompleteLookVariantId(sectionEl);
      if (!mainVariantId || !completeLookVariantId) return null;

      const quantityInput = form.querySelector('input[name="quantity"]');
      const quantity = Math.max(1, parseInt(quantityInput?.value || '1', 10) || 1);

      return {
        items: [
          { id: mainVariantId, quantity },
          { id: completeLookVariantId, quantity: 1 }
        ]
      };
    };

    const addBundle = async (form, button, event) => {
      const bundle = getEligibleBundle(form);
      if (!bundle || addInFlight) return false;

      event?.preventDefault();
      event?.stopImmediatePropagation();

      addInFlight = true;
      const spinner = form.querySelector('.loading-overlay__spinner') || button?.querySelector('.loading-overlay__spinner');

      button?.setAttribute('aria-disabled', 'true');
      button?.classList.add('loading');
      spinner?.classList.remove('hidden');

      try {
        const response = await fetch(getCartAddUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin',
          body: JSON.stringify(bundle)
        }).then((res) => res.json());

        if (response.status) {
          const productForm = form.closest('product-form');
          productForm?.handleErrorMessage?.(response.description);
          return true;
        }

        await openCartAfterCompleteLookAdd(response);
      } catch (error) {
        console.error(error);
      } finally {
        addInFlight = false;
        button?.classList.remove('loading');
        button?.removeAttribute('aria-disabled');
        spinner?.classList.add('hidden');
      }

      return true;
    };

    document.addEventListener('click', (event) => {
      const button = event.target.closest('.product-form__submit[name="add"], #kowi-sticky-atc-btn[name="add"]');
      if (!button || button.hasAttribute('disabled') || button.getAttribute('aria-disabled') === 'true') return;

      const form = button.closest('form[data-type="add-to-cart-form"], form[action*="/cart/add"]');
      if (!form) return;

      addBundle(form, button, event);
    }, true);

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('form[data-type="add-to-cart-form"], form[action*="/cart/add"]')) return;

      const button = form.querySelector('.product-form__submit[name="add"], #kowi-sticky-atc-btn[name="add"]');
      addBundle(form, button, event);
    }, true);
  };

  const initStickyATC = (sectionEl) => {
    const sticky = sectionEl.querySelector('.kowi-sticky-atc');
    if (!sticky || sticky.dataset.bound === 'true') return;
    sticky.dataset.bound = 'true';

    const mq = window.matchMedia('(max-width: 749px)');
    let rafId = null;

    const getMainForm = () => sectionEl.querySelector('form[id^="product-form-"]:not([id^="kowi-sticky-product-form-"])');
    const getMainButton = () => getMainForm()?.querySelector('.product-form__submit');

    const setStickyVisible = (isVisible) => {
      sticky.classList.toggle('is-hidden', !isVisible);
      document.body.classList.toggle('kowi-has-sticky-atc', isVisible);
    };

    const syncStickyVisibility = () => {
      if (!mq.matches) {
        setStickyVisible(false);
        return;
      }

      const stickyBtn = sticky.querySelector('[data-kowi-sticky-btn]');
      if (stickyBtn?.dataset.mode === 'notify') {
        setStickyVisible(false);
        return;
      }

      const target = getMainButton();
      if (!target) {
        setStickyVisible(false);
        return;
      }

      const rect = target.getBoundingClientRect();
      const hasPassedButton = rect.bottom <= 0;
      setStickyVisible(hasPassedButton);
    };

    const scheduleStickySync = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        syncStickyVisibility();
      });
    };

    const enable = () => {
      setStickyVisible(false);
      scheduleStickySync();
    };

    const disable = () => {
      setStickyVisible(false);
    };

    const syncMode = () => (mq.matches ? enable() : disable());
    if (mq.addEventListener) mq.addEventListener('change', syncMode);
    else mq.addListener(syncMode);
    window.addEventListener('scroll', scheduleStickySync, { passive: true });
    window.addEventListener('resize', scheduleStickySync);
    syncMode();

    sectionEl.addEventListener('variant:change', () => {
      syncVariantDrivenUi(sectionEl);
      if (mq.matches) scheduleStickySync();
    });

    sectionEl.addEventListener('change', (event) => {
      if (
        event.target?.matches('variant-radios input[type="radio"]') ||
        event.target?.matches('variant-selects select')
      ) {
        syncVariantDrivenUi(sectionEl);
        if (mq.matches) scheduleStickySync();
      }
    });

    syncVariantDrivenUi(sectionEl);
    scheduleStickySync();
  };

  const initSizeGuides = () => {
    document.querySelectorAll('.kowi-size-guide').forEach((wrap) => {
      if (wrap.dataset.bound === 'true') return;
      wrap.dataset.bound = 'true';

      const buttons = wrap.querySelectorAll('.kowi-size-guide__btn[data-unit]');
      if (!buttons.length) return;

      const setUnit = (unit) => {
        buttons.forEach((button) => {
          button.classList.toggle('is-active', button.dataset.unit === unit);
        });
        wrap.querySelectorAll('.kowi-size-guide__media').forEach((media) => {
          media.classList.toggle('is-hidden', media.dataset.unit !== unit);
        });
      };

      buttons.forEach((button) => {
        button.addEventListener('click', () => setUnit(button.dataset.unit));
      });

      setUnit('cm');
    });
  };

  const initKlaviyoModal = () => {
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-kowi-klaviyo-open]');
      if (opener) {
        const modal = document.getElementById(opener.getAttribute('data-kowi-klaviyo-open'));
        if (typeof modal?.show === 'function') modal.show();
        else if (typeof modal?.open === 'function') modal.open();
        else modal?.setAttribute('open', 'true');
        return;
      }

      const closeButton = event.target.closest('[data-kowi-klaviyo-close]');
      if (closeButton) {
        const modal = closeButton.closest('modal-dialog');
        if (typeof modal?.hide === 'function') modal.hide();
        else if (typeof modal?.close === 'function') modal.close();
        else modal?.removeAttribute('open');
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const modal = document.querySelector('modal-dialog[open], modal-dialog[active]');
      if (!modal) return;
      if (typeof modal.hide === 'function') modal.hide();
      else if (typeof modal.close === 'function') modal.close();
      else modal.removeAttribute('open');
    });
  };

  const initSkipButtons = (sectionEl) => {
    const scroller = sectionEl.querySelector('[id^="Slider-Gallery-"]');
    if (!scroller || scroller.dataset.bound === 'true') return;
    scroller.dataset.bound = 'true';

    const prev = sectionEl.querySelector('.button--image .kowi-skip--prev') || sectionEl.querySelector('.button--image .previus:first-child');
    const next = sectionEl.querySelector('.button--image .kowi-skip--next') || sectionEl.querySelector('.button--image .previus:last-child');

    const step = () => scroller.clientWidth || 0;
    const go = (direction) => {
      const width = step();
      if (!width) return;
      scroller.scrollBy({ left: direction * width, behavior: 'smooth' });
    };

    prev?.addEventListener('click', () => go(-1));
    next?.addEventListener('click', () => go(1));

    const gallery = sectionEl.querySelector('[id^="GalleryViewer-"]') || scroller;
    gallery.setAttribute('tabindex', '0');
    gallery.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') go(-1);
      if (event.key === 'ArrowRight') go(1);
    });
  };

  const initPillVariants = () => {
    document.addEventListener('click', (event) => {
      const staticVariantLabel = event.target.closest('variant-radios[data-disable-variant-interaction="true"] label.pill-variant');
      if (staticVariantLabel) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const soldoutLabel = event.target.closest('label.pill-variant[data-soldout="true"]');
      if (soldoutLabel) {
        event.preventDefault();
        event.stopPropagation();

        const fieldset = soldoutLabel.closest('fieldset');
        fieldset?.querySelectorAll('label.pill-variant.is-selected').forEach((node) => node.classList.remove('is-selected'));
        soldoutLabel.classList.add('is-selected');

        const soldoutInputId = soldoutLabel.getAttribute('for');
        const soldoutInput = soldoutInputId ? document.getElementById(soldoutInputId) : null;
        if (soldoutInput) {
          soldoutInput.checked = true;
          soldoutInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      const label = event.target.closest('label.pill-variant');
      if (!label) return;

      event.preventDefault();

      const fieldset = label.closest('fieldset');
      fieldset?.querySelectorAll('label.pill-variant.is-selected').forEach((node) => node.classList.remove('is-selected'));
      label.classList.add('is-selected');

      const id = label.getAttribute('for');
      const input = id ? document.getElementById(id) : null;
      if (!input) return;

      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  const initBisBridge = () => {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('#BIS');
      if (!trigger) return;

      const realTrigger =
        document.querySelector('#BIS_trigger') ||
        document.querySelector('.BIS_trigger') ||
        document.querySelector('[data-bis-trigger]');

      realTrigger?.click();
    });
  };

  const initSelectSizeGuard = () => {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-mode="select-size"]');
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      const sectionEl = button.closest('[data-pdp-545]') || button.closest('.shopify-section');
      if (!sectionEl) return;

      scrollToSizePicker(sectionEl);
    }, true);
  };

  onReady(() => {
    getSectionRoots().forEach((sectionEl) => {
      sectionEl.querySelectorAll('.kowi-gallery').forEach(initKowiGallery);
      initPdpFullscreenGallery(sectionEl);
      initCompleteLook(sectionEl);
      unlockPrivateSaleSoftAccess(sectionEl);
      initStickyATC(sectionEl);
      initSkipButtons(sectionEl);
      resetInitialSizeSelection(sectionEl);
      syncVariantDrivenUi(sectionEl);
    });

    initSizeGuides();
    initKlaviyoModal();
    initPillVariants();
    initBisBridge();
    initSelectSizeGuard();
    initCompleteLookAddToCartGuard();
  });
})();
